import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { isNewGame, NewBadge } from "@/lib/isNew";
import { EXERCISE_META, BUILTIN_GAMES } from "@/data/exercises";
import MemoryMatch from "@/components/patient/MemoryMatch";
import Recognition from "@/components/patient/Recognition";
import DragMatch from "@/components/patient/DragMatch";
import TapTargets from "@/components/patient/TapTargets";
import PathTrace from "@/components/patient/PathTrace";
import PicWords from "@/components/patient/PicWords";
import Sudoku from "@/components/patient/Sudoku";
import Crossword from "@/components/patient/Crossword";
import Differences from "@/components/patient/Differences";
import QuizPlayer from "@/components/patient/QuizPlayer";
import PuzzlePlayer from "@/components/patient/PuzzlePlayer";
import BlindTestPlayer from "@/components/patient/BlindTestPlayer";
import PronunciationPlayer from "@/components/patient/PronunciationPlayer";
import BreathingPlayer from "@/components/patient/BreathingPlayer";
import ColorsPlayer from "@/components/patient/ColorsPlayer";
import SoundsPlayer from "@/components/patient/SoundsPlayer";
import SuccessScreen from "@/components/patient/SuccessScreen";
import HesitationHelper from "@/components/patient/HesitationHelper";
import { GardienBadge } from "@/components/GardienBadge";
import { gardienForCategory, getGardien, GARDIENS, CATEGORY_SCENE } from "@/data/gardiens";
import FamiliarAlbum from "@/components/patient/FamiliarAlbum";
import { useNavigate } from "react-router-dom";
import GameRecommendation from "@/components/patient/GameRecommendation";
import PrintGameButton from "@/components/patient/PrintGameButton";
import { Brain, Puzzle, MessageCircle, Sparkles, Clock, ChevronLeft, ChevronRight, Loader2, BookOpen } from "lucide-react";

const CAT_STYLE = {
  alzheimer: { icon: Brain, bg: "#23739F", text: "#FFFFFF", accent: "rgba(255,255,255,0.18)" },
  autisme: { icon: Puzzle, bg: "#F8E4C7", text: "#5A3A1E", accent: "rgba(90,58,30,0.10)" },
  aphasie: { icon: MessageCircle, bg: "#DCEFE4", text: "#1F4D36", accent: "rgba(31,77,54,0.10)" },
  sensoriels: { icon: Sparkles, bg: "#FBEAD3", text: "#6B3F14", accent: "rgba(107,63,20,0.10)" },
  routine: { icon: Clock, bg: "#E6EEF5", text: "#1B5878", accent: "rgba(27,88,120,0.10)" },
};

const SESSION_CAT = { quiz: "cognition", comprendre: "cognition", puzzle: "motor", blindtest: "cognition", pronunciation: "cognition", breathing: "cognition", colors: "cognition", sounds: "cognition" };

function BigButton({ onClick, title, sub, testId, bg, text, Icon, accent, image, round, familiar, scene, newCount = 0 }) {
  return (
    <button onClick={onClick} data-testid={testId} style={{ backgroundColor: bg, color: text }}
      className={`w-full min-h-[6.5rem] rounded-3xl p-5 sm:p-6 flex ${scene ? "flex-col sm:flex-row items-stretch sm:items-center" : "items-center"} gap-4 text-left active:scale-[0.98] transition-transform duration-200 border-4 border-black/5 shadow-sm hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgb(0,0,0,0.06)] transition-[transform,box-shadow]`}>
      {scene ? (
        <span className="shrink-0 w-full sm:w-44 aspect-[3/2] rounded-2xl overflow-hidden border-4 border-white/70 shadow-sm">
          <img src={scene} alt="" className="w-full h-full object-cover" data-testid={`${testId}-scene`} />
        </span>
      ) : image ? (
        <span className="relative shrink-0">
          <span className={`block w-16 h-16 ${round ? "rounded-full border-4" : "rounded-2xl border-2"} overflow-hidden border-white/60 bg-white/20`}>
            <img src={image} alt="" className="w-full h-full object-cover object-top" />
          </span>
          {familiar && <img src={familiar} alt="" className="absolute -bottom-1 -right-1 w-[45%] h-[45%] rounded-full object-cover border-2 border-white shadow-md" />}
        </span>
      ) : Icon ? (
        <span className="shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: accent || "rgba(255,255,255,0.25)" }}>
          <Icon className="w-8 h-8" />
        </span>
      ) : null}
      <span className="flex-1 min-w-0">
        <span className="block font-patient font-black text-xl sm:text-2xl">{title}{newCount > 0 && <NewBadge count={newCount} testId={`${testId}-new`} />}</span>
        {sub && <span className="block font-patient font-medium text-base opacity-80 mt-0.5">{sub}</span>}
      </span>
      <ChevronRight className={`w-6 h-6 opacity-60 shrink-0 ${scene ? "hidden sm:block" : ""}`} />
    </button>
  );
}

export default function GamesBrowser({ onGameComplete, gardienId, patientId }) {
  const navigate = useNavigate();
  const [newFamiliar, setNewFamiliar] = useState(null);
  const scrollToMe = useCallback((el) => el && el.scrollIntoView({ behavior: "smooth", block: "start" }), []);
  const [categories, setCategories] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("categories");
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const [playing, setPlaying] = useState(null);
  const [lastScore, setLastScore] = useState(0);

  const load = useCallback(() => {
    Promise.all([api.get("/game-categories"), api.get("/games")])
      .then(([c, g]) => { setCategories(c.data); setGames(g.data); })
      .catch(() => { setCategories([]); setGames([]); })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const gamesForSub = (cat, sub) => {
    const builtins = BUILTIN_GAMES
      .filter((b) => b.main_category === cat.id && b.sub_name === sub.name)
      .map((b) => ({ kind: "builtin", id: b.id, title: b.name, desc: b.desc }));
    const managed = games
      .filter((g) => g.main_category === cat.id && g.subcategory_id === sub.id)
      .map((g) => ({ kind: "managed", id: g.id, title: g.title, desc: g.description, game: g }));
    return [...builtins, ...managed];
  };
  const newIn = (list) => list.filter((it) => it.kind === "managed" && isNewGame(it.game)).length;
  const newInCat = (cat) => cat.subcategories.reduce((n, s) => n + newIn(gamesForSub(cat, s)), 0);

  const handleComplete = async ({ score, accuracy, duration }) => {
    setLastScore(score);
    setNewFamiliar(null);
    if (patientId && playing) {
      try {
        const { data: saved } = await api.post("/sessions", {
          patient_id: patientId,
          category: playing.kind === "managed" ? (SESSION_CAT[playing.game.category] || "cognition") : (EXERCISE_META[playing.id]?.category || "cognition"),
          exercise_id: playing.kind === "managed" ? `managed:${playing.id}` : playing.id,
          exercise_name: playing.name, score: score ?? 0, accuracy: accuracy ?? 0, duration_seconds: duration ?? 0,
        });
        setNewFamiliar(saved.new_familiars?.[0] || null);
      } catch { toast.error("Cette partie n'a pas pu être enregistrée dans le suivi."); }
    }
    if (onGameComplete && playing) {
      const category = playing.kind === "managed"
        ? (SESSION_CAT[playing.game.category] || "cognition")
        : (EXERCISE_META[playing.id]?.category || "cognition");
      onGameComplete({
        exercise_id: playing.kind === "managed" ? `managed:${playing.id}` : playing.id,
        exercise_name: playing.name, category,
        score: score ?? 0, accuracy: accuracy ?? 0, duration: duration ?? 0,
      });
    }
    setView("success");
  };

  const startGame = (item) => {
    if (item.kind === "builtin") setPlaying({ kind: "builtin", id: item.id, name: item.title });
    else setPlaying({ kind: "managed", id: item.id, name: item.title, game: item.game });
    setView("play");
  };

  const renderPlayer = () => {
    if (!playing) return null;
    if (playing.kind === "managed") {
      const g = playing.game;
      switch (g.category) {
        case "quiz":
        case "comprendre": return <QuizPlayer game={g} onComplete={handleComplete} />;
        case "puzzle": return <PuzzlePlayer game={g} onComplete={handleComplete} />;
        case "blindtest": return <BlindTestPlayer game={g} onComplete={handleComplete} />;
        case "pronunciation": return <PronunciationPlayer game={g} onComplete={handleComplete} />;
        case "breathing": return <BreathingPlayer onComplete={handleComplete} />;
        case "colors": return <ColorsPlayer onComplete={handleComplete} />;
        case "sounds": return <SoundsPlayer onComplete={handleComplete} />;
        default: return null;
      }
    }
    switch (playing.id) {
      case "memory": return <MemoryMatch onComplete={handleComplete} />;
      case "recognition": return <Recognition onComplete={handleComplete} />;
      case "dragmatch": return <DragMatch onComplete={handleComplete} />;
      case "taptargets": return <TapTargets onComplete={handleComplete} />;
      case "pathtrace": return <PathTrace onComplete={handleComplete} />;
      case "picwords": return <PicWords onComplete={handleComplete} />;
      case "crossword": return <Crossword onComplete={handleComplete} />;
      case "sudoku_facile": return <Sudoku level="facile" onComplete={handleComplete} />;
      case "sudoku_moyen": return <Sudoku level="moyen" onComplete={handleComplete} />;
      case "differences": return <Differences onComplete={handleComplete} />;
      default: return null;
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-emerald-700" /></div>;

  if (view === "success") {
    return (
      <div className="max-w-2xl mx-auto scroll-mt-24" ref={scrollToMe}>
        <SuccessScreen newFamiliar={newFamiliar} gardienId={gardienId || (category ? gardienForCategory(category.id).id : "leontine")} score={lastScore} onHome={() => setView("games")} onAgain={() => setView("play")} />
      </div>
    );
  }

  const back = () => {
    if (view === "familiars") setView("categories");
    else if (view === "play") setView("games");
    else if (view === "games") setView("subcategories");
    else if (view === "subcategories") setView("categories");
  };

  return (
    <div data-testid="family-games-browser">
      {view !== "categories" && (
        <button onClick={back} data-testid="games-browser-back"
          className="inline-flex items-center gap-2 mb-6 h-11 px-5 rounded-full bg-white border-2 border-emerald-200 font-patient font-bold text-emerald-700">
          <ChevronLeft className="w-5 h-5" /> Retour
        </button>
      )}

      {view === "categories" && (
        <div className="flex flex-col gap-5 max-w-2xl">
          {categories.map((c) => {
            const st = CAT_STYLE[c.id] || CAT_STYLE.alzheimer;
            return (
              <BigButton key={c.id} testId={`family-category-${c.id}`} title={c.label}
                sub={`Avec ${gardienForCategory(c.id).name} · ${c.subcategories.length ? `${c.subcategories.length} sous-catégorie(s)` : "Contenu à venir"}`}
                bg={st.bg} text={st.text} Icon={st.icon} accent={st.accent} image={gardienForCategory(c.id).image} familiar={gardienForCategory(c.id).familiar?.image} round scene={CATEGORY_SCENE(c.id)} newCount={newInCat(c)}
                onClick={() => { setCategory(c); setView("subcategories"); }} />
            );
          })}
          <BigButton testId="family-stories-button" title="Contes & Poésie" sub="Avec Arsène · un moment de lecture douce"
            bg="#FFFFFF" text="#273643" Icon={BookOpen} image={gardienForCategory("contes").image} familiar={gardienForCategory("contes").familiar?.image} round
            onClick={() => navigate(patientId ? `/contes?patient=${patientId}` : "/contes")} />
          {patientId && (
            <BigButton testId="family-familiars-button" title="L'album des familiers" sub="Un nouveau petit compagnon toutes les 5 activités"
              bg="#FFFFFF" text="#273643" Icon={Sparkles} image={getGardien(gardienId).familiar?.image || GARDIENS[0].familiar.image} round
              onClick={() => setView("familiars")} />
          )}
        </div>
      )}

      {view === "subcategories" && category && (
        <div className="max-w-2xl">
          <h3 className="text-3xl font-serif-display italic text-gray-800 mb-5">{category.label}</h3>
          <GardienBadge id={gardienForCategory(category.id).id} eyebrow="Votre Gardien vous accompagne" className="mb-6" testId="family-category-gardien" />
          {category.subcategories.length === 0 ? (
            <div className="rounded-3xl bg-white border-4 border-dashed border-gray-300 p-10 text-center" data-testid="family-subcategories-empty">
              <p className="text-xl font-patient font-bold text-gray-400">Contenu à venir</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {category.subcategories.map((s) => {
                const count = gamesForSub(category, s).length;
                return (
                  <BigButton key={s.id} testId={`family-subcategory-${s.id}`} title={s.name}
                    sub={count ? `${count} jeu(x)` : "Contenu à venir"} bg="#FFFFFF" text="#273643" image={s.image} newCount={newIn(gamesForSub(category, s))}
                    onClick={() => { setSubcategory(s); setView("games"); }} />
                );
              })}
            </div>
          )}
        </div>
      )}

      {view === "games" && category && subcategory && (
        <div className="max-w-2xl">
          <h3 className="text-3xl font-serif-display italic text-gray-800 mb-5">{subcategory.name}</h3>
          {gamesForSub(category, subcategory).length === 0 ? (
            <div className="rounded-3xl bg-white border-4 border-dashed border-gray-300 p-10 text-center" data-testid="family-games-empty">
              <p className="text-xl font-patient font-bold text-gray-400">Contenu à venir</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {gamesForSub(category, subcategory).map((item) => (
                <div key={`${item.kind}-${item.id}`} className="space-y-3">
                  <BigButton testId={`family-game-${item.id}`} title={item.title} newCount={item.kind === "managed" && isNewGame(item.game) ? 1 : 0}
                    sub={item.desc} bg="#FFFFFF" text="#273643" onClick={() => startGame(item)} />
                  <PrintGameButton game={item.kind === "managed" ? item.game : { id: item.id, title: item.title, builtin: true }} />
                  {item.kind === "managed" && <GameRecommendation game={item.game} />}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === "play" && (
        <div className="py-2" key={playing?.id}>
          {renderPlayer()}
          {playing?.game?.category !== "breathing" && (
            <HesitationHelper gardienId={gardienId || (category ? gardienForCategory(category.id).id : "leontine")} />
          )}
        </div>
      )}
      {view === "familiars" && patientId && <FamiliarAlbum patientId={patientId} />}
    </div>
  );
}
